/**
 * Who wrote each bundled translation — the admin translation table's fourth badge state
 * (POS2-1150).
 *
 * `translate_catalog.py` writes `messages/<locale>/.source-hashes.json`, whose entries carry the
 * English digest change detection reads and a `by` mark naming the author. This module is the
 * only reader of the `by` half.
 *
 * **Deliberately not part of `catalog.ts`.** That module's glob excludes dotfiles precisely to
 * keep this file out of the runtime chain, and merging the two would put a map of sha256 digests
 * into every user's bundle to serve one staff page.
 *
 * **One importer is necessary but not sufficient**, and that is worth stating because the first
 * version of this branch got it wrong. Being imported by a single lazily-routed page is a
 * *source-level* property; which chunk the JSON lands in is decided by `manualChunks` in
 * `vite-chunks.ts`, which matched on the locale directory and swept this dotfile into
 * `messages-de` — precached by the service worker, so every user fetched 65 kB gzip of sha256
 * digests at install. Two tests hold the two halves: `provenance.test.ts` the importer,
 * `locale-chunk.test.ts` the chunk rule.
 *
 * A `Set` of the keys a model wrote rather than a key → author map, because the table has two
 * states and not three: `human` and "no entry at all" both mean nobody has been told a model
 * wrote it, and both read as `reviewed`. A missing or mangled file therefore degrades to an empty
 * set — every row `reviewed` — rather than breaking the page.
 */

/** One entry of the hash file. `en` belongs to the script's change detection, not here. */
interface HashEntry {
  en?: string;
  by?: string;
}

const loaders = import.meta.glob<Record<string, HashEntry>>("./messages/*/.source-hashes.json", {
  import: "default",
});

/** The keys a model wrote, out of a parsed hash file.
 *
 * Separate from the load so it is testable without a fixture file in `messages/`: a real
 * `.source-hashes.json` cannot be committed for a test, because its `en` digests are what the
 * script's change detection reads — a fake one would tell the first real run that everything is
 * already up to date and translate nothing.
 */
export function provenanceFrom(entries: Record<string, HashEntry>): Set<string> {
  return new Set(
    Object.entries(entries)
      // `entry?.by` rather than a shape assertion: this file is generated, and a bad merge is
      // what produces a string where an object belongs. A row nobody can classify is `reviewed`.
      .filter(([, entry]) => entry?.by === "claude")
      .map(([key]) => key),
  );
}

/** The catalogue keys a model translated, as `ns.key`. Empty when the locale has no hash file. */
export async function loadProvenance(locale: string): Promise<Set<string>> {
  const load = loaders[`./messages/${locale}/.source-hashes.json`];
  if (!load) return new Set();
  return provenanceFrom(await load());
}
